import { useFrame, useLoader, useThree } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';
import { throttle } from 'lodash';
import { placeholder } from '../placeholder';
import { ViewerPerformanceConfig } from '../types';
import { useChat3DStore } from 'stores/chat3DStore';

const DEFAULT_PERFORMANCE_CONFIG: ViewerPerformanceConfig = {
  maxFPS: 60,
  rayCastThrottle: 50,
  enableHoverIndicator: true,
};

export function useViewer(url: string, config: Partial<ViewerPerformanceConfig> = {}) {
  const performanceConfig = { ...DEFAULT_PERFORMANCE_CONFIG, ...config };
  const { measureMode, addMeasurementPoint } = useChat3DStore();

  // Refs for performance optimization
  const meshRef = useRef<THREE.Mesh | null>(null);
  const hoverIndicatorRef = useRef<THREE.Group | null>(null);
  const lastRaycastTime = useRef(0);

  // Three.js hooks
  const { scene, camera, raycaster, pointer, gl } = useThree();

  // Load geometry
  const geometry = useLoader(STLLoader, url);

  // Materials
  const materials = useMemo(
    () => ({
      model: new THREE.MeshStandardMaterial({
        color: '#009688',
        roughness: 0.5,
        metalness: 0.5,
        side: THREE.DoubleSide,
      }),
      hover: new THREE.MeshBasicMaterial({
        color: 'yellow',
        transparent: true,
        opacity: 0.5,
        depthTest: false,
      }),
    }),
    []
  );

  // Setup scene
  useEffect(() => {
    if (!geometry) return;

    try {
      // Setup model mesh
      geometry.center();
      geometry.computeVertexNormals();
      const mesh = new THREE.Mesh(geometry, materials.model);

      if (meshRef.current) {
        scene.remove(meshRef.current);
      }

      meshRef.current = mesh;
      scene.add(mesh);

      // Setup camera
      const box = new THREE.Box3().setFromObject(mesh);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());

      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = (camera as THREE.PerspectiveCamera).fov * (Math.PI / 180);
      const cameraZ = Math.abs(maxDim / Math.sin(fov / 2) / 2);
      camera.position.set(0, 0, cameraZ * 1.5);
      camera.lookAt(center);
      camera.updateProjectionMatrix();

      // Setup hover indicator
      if (performanceConfig.enableHoverIndicator) {
        if (hoverIndicatorRef.current) {
          scene.remove(hoverIndicatorRef.current);
        }
        const hoverGroup = new THREE.Group();
        const sphere = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 16), materials.hover);
        hoverGroup.add(sphere);
        hoverGroup.visible = false;
        hoverGroup.renderOrder = 1;
        hoverIndicatorRef.current = hoverGroup;
        scene.add(hoverGroup);
      }
    } catch (error) {
      console.error('Error setting up model:', error);
    }

    return () => {
      if (meshRef.current) {
        scene.remove(meshRef.current);
        meshRef.current = null;
      }
      if (hoverIndicatorRef.current) {
        scene.remove(hoverIndicatorRef.current);
        hoverIndicatorRef.current = null;
      }
      geometry.dispose();
      Object.values(materials).forEach(material => material.dispose());
    };
  }, [geometry, scene, camera, materials, performanceConfig.enableHoverIndicator]);

  // Throttled raycasting
  const performRaycast = useCallback(
    throttle((pointer: THREE.Vector2, camera: THREE.Camera) => {
      if (!meshRef.current || !measureMode) return null;
      raycaster.setFromCamera(pointer, camera);
      const intersects = raycaster.intersectObject(meshRef.current, true);
      return intersects[0]?.point || null;
    }, performanceConfig.rayCastThrottle),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [measureMode] // Omitting stable dependencies raycaster and performanceConfig.rayCastThrottle
  );

  // Frame update for hover indicator
  useFrame((_, delta) => {
    if (!measureMode || !performanceConfig.enableHoverIndicator) {
      if (hoverIndicatorRef.current) {
        hoverIndicatorRef.current.visible = false;
      }
      return;
    }

    const currentTime = performance.now();
    if (currentTime - lastRaycastTime.current < 1000 / performanceConfig.maxFPS) {
      return;
    }
    lastRaycastTime.current = currentTime;

    const intersectionPoint = performRaycast(pointer, camera);
    if (intersectionPoint && hoverIndicatorRef.current) {
      const distance = intersectionPoint.distanceTo(camera.position);
      const size =
        Math.tan(((camera as THREE.PerspectiveCamera).fov * Math.PI) / 180 / 2) * distance * 0.02;
      hoverIndicatorRef.current.position.copy(intersectionPoint);
      hoverIndicatorRef.current.scale.setScalar(size);
      hoverIndicatorRef.current.visible = true;
    } else if (hoverIndicatorRef.current) {
      hoverIndicatorRef.current.visible = false;
    }
  });

  // Click handler
  const handleClick = useCallback(
    (event: { stopPropagation: () => void }) => {
      if (!measureMode || !meshRef.current) return;
      event.stopPropagation();
      const intersectionPoint = performRaycast(pointer, camera);
      if (intersectionPoint) {
        addMeasurementPoint(intersectionPoint);
      }
    },
    [measureMode, addMeasurementPoint, performRaycast, pointer, camera]
  );

  // Capture current view as image
  const captureView = useCallback(() => {
    if (!scene || !camera || !gl) return null;

    // Render scene
    gl.render(scene, camera);

    // Get base64 image data
    const imageData = gl.domElement.toDataURL('image/png');
    return imageData;
  }, [scene, camera, gl]);

  return {
    handleClick,
    captureView,
    isLoading: !geometry,
    scene,
    meshRef,
  };
}
